import { useEffect, useState } from 'react';
import { createCompanyService, deleteCompanyService, getCompanyServices, updateCompanyService } from '../services/api.js';

const ServicesManagement = ({ authToken }) => {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'other',
    price: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const categories = [
    { key: 'preventive', label: 'Preventive Care' },
    { key: 'cosmetic', label: 'Cosmetic' },
    { key: 'restorative', label: 'Restorative' },
    { key: 'consultation', label: 'Consultation' },
    { key: 'emergency', label: 'Emergency' },
    { key: 'other', label: 'Other Services' }
  ];

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCompanyServices(authToken);
      // Handle paginated response from DRF
      let servicesList;
      if (response && response.results && Array.isArray(response.results)) {
        servicesList = response.results;
      } else if (Array.isArray(response)) {
        servicesList = response;
      } else {
        console.warn('Unexpected response format:', response);
        servicesList = [];
      }
      setServices(servicesList);
      setSuccessMessage(null);
    } catch (err) {
      console.error('Failed to fetch services:', err);
      // Don't show error if it's a 401 (auth error) - the interceptor will handle redirect
      if (err.response?.status !== 401) {
        setError(err.response?.data?.detail || err.message || 'Failed to fetch services');
      }
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter and search services
  useEffect(() => {
    let filtered = services;
    
    // Search by name or description
    if (searchTerm) {
      filtered = filtered.filter(service =>
        service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (service.description && service.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Filter by category
    if (categoryFilter) {
      filtered = filtered.filter(service => service.category === categoryFilter);
    }
    
    setFilteredServices(filtered);
  }, [services, searchTerm, categoryFilter]);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Service name is required';
    } else if (formData.name.length > 100) {
      errors.name = 'Service name must be less than 100 characters';
    }
    
    if (formData.price && (isNaN(formData.price) || parseFloat(formData.price) < 0)) {
      errors.price = 'Price must be a valid positive number';
    }
    
    // Check for duplicate names
    const isDuplicate = services.some(service =>
      service.name.toLowerCase() === formData.name.toLowerCase() &&
      service.id !== editingService?.id
    );
    
    if (isDuplicate) {
      errors.name = 'A service with this name already exists';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    // Check if we have an access_token in localStorage (the actual JWT token)
    const hasToken = localStorage.getItem('access_token');

    if (hasToken) {
      fetchServices();
    } else {
      // No token available - don't show error, just stop loading
      // The interceptor will redirect to login if needed
      console.warn('No access token available for fetching services');
      setLoading(false);
      setServices([]);
    }
  }, []); // Only run once on mount

  // Listen for changes in authToken (after login)
  useEffect(() => {
    if (authToken && services.length === 0 && !loading) {
      // Token is now available and we haven't loaded services yet
      fetchServices();
    }
  }, [authToken]); // Re-run when authToken changes

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      const serviceData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category,
        price: formData.price ? parseFloat(formData.price) : null,
        is_active: true
      };
      
      if (editingService) {
        await updateCompanyService(authToken, editingService.id, serviceData);
        setSuccessMessage('Service updated successfully!');
      } else {
        await createCompanyService(authToken, serviceData);
        setSuccessMessage('Service created successfully!');
      }
      
      setFormData({ name: '', description: '', category: 'other', price: '' });
      setFormErrors({});
      setEditingService(null);
      setShowCreateForm(false);
      fetchServices();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save service:', err);
      if (err.response?.data?.name) {
        setFormErrors({ name: err.response.data.name[0] });
      } else if (err.response?.data?.price) {
        setFormErrors({ price: err.response.data.price[0] });
      } else {
        setError(err.response?.data?.detail || 'Failed to save service');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description || '',
      category: service.category || 'other',
      price: service.price || ''
    });
    setFormErrors({});
    setError(null);
    setShowCreateForm(true);
  };

  const handleDelete = async (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (window.confirm(`Are you sure you want to delete the service "${service?.name}"? This action cannot be undone.`)) {
      try {
        setSaving(true);
        await deleteCompanyService(authToken, serviceId);
        setSuccessMessage('Service deleted successfully!');
        fetchServices();
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (err) {
        console.error('Failed to delete service:', err);
        setError(err.response?.data?.detail || 'Failed to delete service');
      } finally {
        setSaving(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', category: 'other', price: '' });
    setFormErrors({});
    setEditingService(null);
    setShowCreateForm(false);
    setError(null);
  };

  const getCategoryLabel = (categoryKey) => {
    const category = categories.find(cat => cat.key === categoryKey);
    return category ? category.label : categoryKey;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 pt-24">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Services Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage your company's services and pricing
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          disabled={saving}
          className="bg-secondary text-white px-4 py-2 rounded-md hover:bg-secondary/90/85 focus:outline-none focus:ring-2 focus:ring-text-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          Create Service
        </button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md mb-4 flex items-center">
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-4 flex items-center">
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {error}
        </div>
      )}

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-border p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Services
            </label>
            <input
              type="text"
              placeholder="Search by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-text-primary focus:border-text-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Category
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-text-primary focus:border-text-primary"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.key} value={category.key}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <div className="text-sm text-gray-600">
              Showing {filteredServices.length} of {services.length} services
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-sm border border-border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {editingService ? "Edit Service" : "Create New Service"}
            </h2>
            <button
              onClick={resetForm}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-text-primary ${
                    formErrors.name
                      ? "border-red-300 focus:border-red-500"
                      : "border-border focus:border-text-primary"
                  }`}
                  placeholder="e.g., Teeth Cleaning"
                />
                {formErrors.name && (
                  <p className="text-red-600 text-sm mt-1">{formErrors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-text-primary focus:border-text-primary"
                >
                  {categories.map((category) => (
                    <option key={category.key} value={category.key}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price ($)
                </label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-text-primary ${
                    formErrors.price
                      ? "border-red-300 focus:border-red-500"
                      : "border-border focus:border-text-primary"
                  }`}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
                {formErrors.price && (
                  <p className="text-red-600 text-sm mt-1">
                    {formErrors.price}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows="3"
                  className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-text-primary focus:border-text-primary"
                  placeholder="Optional description of the service"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                type="submit"
                disabled={saving}
                className="bg-secondary text-white px-6 py-2 rounded-md hover:bg-secondary/90/85 focus:outline-none focus:ring-2 focus:ring-text-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {saving && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                {editingService ? "Update Service" : "Create Service"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                disabled={saving}
                className="bg-accent-teal-subtle text-gray-700 px-6 py-2 rounded-md hover:bg-background focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Services List */}
      <div className="space-y-4">
        {filteredServices.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-border">
            {services.length === 0 ? (
              <>
                <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No services yet
                </h3>
                <p className="text-gray-600 mb-4">
                  Create your first service to get started
                </p>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="bg-secondary text-white px-4 py-2 rounded-md hover:bg-secondary/90/85 focus:outline-none focus:ring-2 focus:ring-text-primary"
                >
                  Create Your First Service
                </button>
              </>
            ) : (
              <>
                <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No services found
                </h3>
                <p className="text-gray-600">
                  Try adjusting your search or filter criteria
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredServices.map((service) => (
              <div
                key={service.id}
                className="bg-white border border-border rounded-lg p-6 hover:shadow-sm transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {service.name}
                      </h3>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-teal-subtle text-teal-800">
                        {getCategoryLabel(service.category)}
                      </span>
                      {service.price && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ${parseFloat(service.price).toFixed(0)}
                        </span>
                      )}
                    </div>

                    {service.description && (
                      <div className="bg-background rounded-md p-3 mb-3">
                        <p className="text-sm text-gray-700">
                          {service.description}
                        </p>
                      </div>
                    )}

                    <div className="flex items-center text-xs text-gray-500 space-x-4">
                      <span>
                        Created:{" "}
                        {new Date(service.created_at).toLocaleDateString()}
                      </span>
                      {service.updated_at !== service.created_at && (
                        <span>
                          Updated:{" "}
                          {new Date(service.updated_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(service)}
                      disabled={saving}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary hover:text-teal-800 hover:bg-accent-teal-light-bg rounded-md transition-colors disabled:opacity-50"
                    >
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(service.id)}
                      disabled={saving}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                    >
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesManagement;